import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CodeIcon from '@mui/icons-material/Code';
import { CmsProject, CmsFaqItem } from '../../types/cms';

const GOLD = '#9d6f32';
const BORDER = '#e8e8e8';

interface ProjectFaqTabProps {
  project: CmsProject;
  onChange: (updated: CmsProject) => void;
}

export const ProjectFaqTab: React.FC<ProjectFaqTabProps> = ({ project, onChange }) => {
  const faqs = project.faqs || [];

  const handleAddFaq = () => {
    const newFaq: CmsFaqItem = {
      id: `f_${Date.now()}`,
      question: 'Frequently asked question about this project?',
      answer: 'Clear, informative answer for buyers and Google search snippets.',
      order: faqs.length + 1,
    };
    onChange({ ...project, faqs: [...faqs, newFaq] });
  };

  const handleFaqChange = (id: string, field: keyof CmsFaqItem, val: string) => {
    const next = faqs.map((f) => (f.id === id ? { ...f, [field]: val } : f));
    onChange({ ...project, faqs: next });
  };

  const handleRemoveFaq = (id: string) => {
    const next = faqs.filter((f) => f.id !== id);
    onChange({ ...project, faqs: next });
  };

  // Google FAQ Schema JSON-LD
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((f) => ({
      '@type': 'Question',
      'name': f.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': f.answer,
      },
    })),
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
      {/* 1. Dynamic FAQs Builder */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpOutlineIcon sx={{ color: GOLD }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                Frequently Asked Questions (Buyer FAQs)
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
                Answer real buyer questions clearly. Google no longer shows FAQ rich results.
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddFaq}
            sx={{
              borderColor: GOLD,
              color: GOLD,
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(157,111,50,0.06)' },
            }}
          >
            Add FAQ
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {faqs.map((faq, idx) => (
            <Paper
              key={faq.id}
              sx={{
                p: 2.5,
                bgcolor: '#fafafa',
                border: `1px solid ${BORDER}`,
                borderRadius: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: GOLD }}>
                  Question #{idx + 1}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFaq(faq.id)}
                  disabled={faqs.length <= 1}
                  sx={{ color: '#ef4444' }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>

              <TextField
                fullWidth
                size="small"
                label="Question"
                value={faq.question}
                onChange={(e) => handleFaqChange(faq.id, 'question', e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                label="Answer"
                value={faq.answer}
                onChange={(e) => handleFaqChange(faq.id, 'answer', e.target.value)}
              />
            </Paper>
          ))}
        </Box>
      </Paper>

      {/* 2. Live Accordion Preview */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1a1a1a' }}>
          Live Website Accordion Preview
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b6b6b', mb: 2 }}>
          How the interactive FAQ section looks to visitors on your project page.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {faqs.map((faq) => (
            <Accordion key={faq.id} sx={{ border: `1px solid ${BORDER}`, boxShadow: 'none', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography sx={{ color: '#4b5563', fontSize: 13.5, lineHeight: 1.5 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Paper>

      {/* 3. Google FAQ Schema (JSON-LD) */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CodeIcon sx={{ color: GOLD }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            FAQ structured data preview
          </Typography>
        </Box>
        <Box
          component="pre"
          sx={{
            p: 2,
            bgcolor: '#1e1e1e',
            color: '#38bdf8',
            borderRadius: 1.5,
            fontSize: 12,
            overflowX: 'auto',
            fontFamily: 'monospace',
            maxHeight: 240,
          }}
        >
          {JSON.stringify(faqSchema, null, 2)}
        </Box>
      </Paper>
    </Box>
  );
};
